import {
  ClientSubscriptionFreezeStatus,
  ClientSubscriptionFreezeType,
  PlanCategory,
} from '@fitness-force/enum';
import type {
  ClientSubscriptionFreezeResponse,
  GetClientSubscriptionResponse,
  InvoiceOccurance,
} from '@fitness-force/models';

export interface YearWiseFreezeAttempt {
  year: number;
  freezeattempt: number;
  totalfreeze: number;
  used: number;
}

export function isActiveFreezeRecord(
  freeze: ClientSubscriptionFreezeResponse
): boolean {
  const isCancelled =
    freeze.status === ClientSubscriptionFreezeStatus.FreezeCancelled ||
    freeze.status?.toString() ===
      ClientSubscriptionFreezeStatus[
        ClientSubscriptionFreezeStatus.FreezeCancelled
      ];
  const isStopped =
    freeze.status === ClientSubscriptionFreezeStatus.FreezeStopped ||
    freeze.status?.toString() ===
      ClientSubscriptionFreezeStatus[
        ClientSubscriptionFreezeStatus.FreezeStopped
      ];
  return !isCancelled && !isStopped;
}

export function countActiveFreezesForYear(
  freezes: ClientSubscriptionFreezeResponse[],
  subscriptionId: number,
  year: number
): number {
  return freezes.filter((f) => {
    if (f.client_subscription?.id !== subscriptionId) {
      return false;
    }
    if (!isActiveFreezeRecord(f)) {
      return false;
    }
    return new Date(f.start_date).getFullYear() === year;
  }).length;
}

export function isRecurringPlanCategory(
  category: PlanCategory | string
): boolean {
  return (
    category === PlanCategory.Contract ||
    category?.toString() === PlanCategory[PlanCategory.Contract] ||
    category === PlanCategory.PayAsYouGo ||
    category?.toString() === PlanCategory[PlanCategory.PayAsYouGo]
  );
}

/** Billing cycles whose start falls within the subscription term. */
export function filterBillingOccurrencesWithinSubscription(
  billingOccurrences: InvoiceOccurance[],
  subscription: GetClientSubscriptionResponse
): InvoiceOccurance[] {
  if (!billingOccurrences.length) {
    return billingOccurrences;
  }

  const subStart = subscription.start_date
    ? new Date(subscription.start_date)
    : null;
  const subEnd = subscription.end_date ? new Date(subscription.end_date) : null;

  if (!subStart && !subEnd) {
    return billingOccurrences;
  }

  return billingOccurrences.filter((item) => {
    const occStart = new Date(item.occurance_start_date);
    if (subStart && occStart < subStart) {
      return false;
    }
    if (subEnd && occStart > subEnd) {
      return false;
    }
    return true;
  });
}

export function getYearWiseFreezeRemaining(options: {
  subscription: GetClientSubscriptionResponse;
  billingOccurrences?: InvoiceOccurance[];
  freezes?: ClientSubscriptionFreezeResponse[];
  paidInFullUsedAttempts?: number;
}): YearWiseFreezeAttempt[] {
  const {
    subscription,
    billingOccurrences = [],
    freezes = [],
    paidInFullUsedAttempts = 0,
  } = options;
  const yearWiseFreezeCount: YearWiseFreezeAttempt[] = [];

  if (
    subscription.freeze_allowed_number_of_attempts_in_year <= 0 ||
    subscription.freeze_type === ClientSubscriptionFreezeType.NoFreeze
  ) {
    return yearWiseFreezeCount;
  }

  const allowed = subscription.freeze_allowed_number_of_attempts_in_year;

  if (isRecurringPlanCategory(subscription.subscription_category)) {
    const inTermOccurrences = filterBillingOccurrencesWithinSubscription(
      billingOccurrences,
      subscription
    );

    let years = inTermOccurrences
      .map((item) => new Date(item.occurance_start_date).getFullYear())
      .filter((value, index, self) => self.indexOf(value) === index);

    const subStartYear = subscription.start_date
      ? new Date(subscription.start_date).getFullYear()
      : null;
    const subEndYear = subscription.end_date
      ? new Date(subscription.end_date).getFullYear()
      : null;

    years = years.filter((year) => {
      if (subStartYear !== null && year < subStartYear) {
        return false;
      }
      if (subEndYear !== null && year > subEndYear) {
        return false;
      }
      return true;
    });

    years.forEach((year: number) => {
      const used = countActiveFreezesForYear(freezes, subscription.id, year);
      const remaining = used >= allowed ? 0 : allowed - used;
      yearWiseFreezeCount.push({
        year,
        freezeattempt: remaining,
        totalfreeze: allowed,
        used,
      });
    });
  } else if (
    subscription.subscription_category === PlanCategory.PaidInFull ||
    subscription.subscription_category?.toString() ===
      PlanCategory[PlanCategory.PaidInFull]
  ) {
    const remaining =
      paidInFullUsedAttempts >= allowed ? 0 : allowed - paidInFullUsedAttempts;
    yearWiseFreezeCount.push({
      year: 0,
      freezeattempt: remaining,
      totalfreeze: allowed,
      used: paidInFullUsedAttempts,
    });
  }

  return yearWiseFreezeCount;
}

export function hasAnyRemainingFreezeAttempts(
  yearWiseData: YearWiseFreezeAttempt[]
): boolean {
  return yearWiseData.some((y) => y.freezeattempt > 0);
}

/** True when the current calendar year is listed and has no attempts left. */
export function isCurrentYearFreezeExhausted(
  yearWiseData: YearWiseFreezeAttempt[],
  referenceDate: Date = new Date()
): boolean {
  const currentYear = referenceDate.getFullYear();
  const currentYearEntry = yearWiseData.find((y) => y.year === currentYear);
  return !!currentYearEntry && currentYearEntry.freezeattempt <= 0;
}

/** Whether the member can open the freeze flow (at least one year must have attempts). */
export function canStartFreezeFlow(
  yearWiseData: YearWiseFreezeAttempt[],
  referenceDate: Date = new Date()
): { allowed: boolean; exhaustedYear?: number } {
  if (!yearWiseData.length) {
    return { allowed: false };
  }

  if (!hasAnyRemainingFreezeAttempts(yearWiseData)) {
    const currentYear = referenceDate.getFullYear();
    const currentYearEntry = yearWiseData.find((y) => y.year === currentYear);
    return {
      allowed: false,
      exhaustedYear: currentYearEntry?.year ?? yearWiseData[0]?.year,
    };
  }

  return { allowed: true };
}

export function getFreezeTargetYears(options: {
  isRecurring: boolean;
  freezeCanOnlyBillingCycle: boolean;
  selectedAttempts: Array<{ occurance_start_date: string | Date }> | null;
  startDate: Date | null;
}): number[] {
  const years = new Set<number>();

  if (
    options.isRecurring &&
    options.freezeCanOnlyBillingCycle &&
    options.selectedAttempts?.length
  ) {
    options.selectedAttempts.forEach((attempt) => {
      years.add(new Date(attempt.occurance_start_date).getFullYear());
    });
  } else if (options.startDate) {
    years.add(new Date(options.startDate).getFullYear());
  }

  return [...years];
}

/** Validates that each target calendar year still has remaining freeze attempts. */
export function validateFreezeYearsHaveRemainingAttempts(
  yearWiseData: YearWiseFreezeAttempt[],
  targetYears: number[]
): { valid: boolean; exhaustedYear?: number } {
  for (const year of targetYears) {
    const entry = yearWiseData.find((y) => y.year === year);
    if (entry && entry.freezeattempt <= 0) {
      return { valid: false, exhaustedYear: year };
    }
  }
  return { valid: true };
}
